import React, { useEffect, useState, useRef, useCallback } from "react";
import cx from "classnames";
import PropTypes from "prop-types";
import moment from "moment";

import "react-calendar/dist/Calendar.css";
import CustomCalendar from "react-calendar";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { Icon } from "./icon";
import global from "../../scss/dhp.scss";

let style = Object.assign({}, global);

const CountdownCalender = ({
  dateFormat,
  updateState,
  id,
  value: controlledValue, // controlled
  defaultValue, // fallback
  usedForType,
  hideOnscroll = false,
  minDate,
  maxDate,
  isDisabled = false,
}) => {
  const ref = useRef(null);

  const [value, setValue] = useState("");
  const [calendarValue, setCalendarValue] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);

  useOnClickOutside(ref, () => setShowCalendar(false));

  const isInvalidDate = val => val instanceof Date && isNaN(val.getTime());

  // SINGLE SOURCE RESOLUTION (controlled > defaultValue)
  useEffect(() => {
    const sourceValue = controlledValue !== undefined ? controlledValue : defaultValue;

    if (!sourceValue) {
      setValue("");
      return;
    }

    const parsed = moment(sourceValue, dateFormat).toDate();

    if (!isInvalidDate(parsed)) {
      setCalendarValue(parsed);
      setValue(sourceValue);
    }
  }, [controlledValue, defaultValue, dateFormat]);

  // prevent initial unwanted update
  const isFirst = useRef(true);

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }

    updateState(value);
  }, [value]);

  // FORMAT HELPERS (same as your logic)
  const handleDDMMYYYY = (input, sep) => {
    if (input.length === 1 && parseInt(input) > 3) input = "0" + input;

    if (input.length === 3 && parseInt(input[2]) > 1) input = input.slice(0, 2) + "0" + input.slice(2);

    if (input.length >= 2) {
      const d = parseInt(input.slice(0, 2));
      let m = parseInt(input.slice(2, 4));

      if (m === 2 && d > 29) input = "29" + sep + input.slice(2);
      else if ([4, 6, 9, 11].includes(m) && d > 30) input = "30" + sep + input.slice(2);
      else if (d > 31) input = "31" + sep + input.slice(2);
      else input = ("0" + d).slice(-2) + sep + input.slice(2);
    }

    if (input.length >= 5) {
      let m = parseInt(input.slice(3, 5));
      m = m > 12 ? 12 : m;
      input = input.slice(0, 3) + ("0" + m).slice(-2) + sep + input.slice(5);
    }

    return input;
  };

  const handleMMDDYYYY = (input, sep) => {
    if (input.length === 1 && parseInt(input) > 1) input = "0" + input;

    if (input.length >= 2) {
      let m = parseInt(input.slice(0, 2));
      m = m > 12 ? 12 : m;
      input = ("0" + m).slice(-2) + sep + input.slice(2);
    }

    if (input.length >= 5) {
      let d = parseInt(input.slice(3, 5));
      const m = parseInt(input.slice(0, 2));
      const max = new Date(new Date().getFullYear(), m, 0).getDate();
      d = d > max ? max : d;
      input = input.slice(0, 3) + ("0" + d).slice(-2) + sep + input.slice(5);
    }

    return input;
  };

  // HANDLE INPUT TYPE
  const handleChange = input => {
    const clean = input.replace(/\D/g, "");
    const sep = dateFormat.substring(2, 3);
    const format = dateFormat.replaceAll(sep, "");

    const formatted = format === "MMDDYYYY" ? handleMMDDYYYY(clean, sep) : handleDDMMYYYY(clean, sep);

    const parsed = moment(formatted, dateFormat).toDate();

    if (!isInvalidDate(parsed)) {
      setCalendarValue(parsed);
    }

    setValue(formatted);
  };

  // HANDLE CALENDAR SELECT
  const onChange = date => {
    const formatted = moment(date).format(dateFormat);
    setCalendarValue(date);
    setValue(formatted);
  };

  const handleInputClick = () => {
    if (usedForType === "Proposal") {
      setShowCalendar(prev => !prev);
      return;
    }
    setShowCalendar(true);
  };

  const handleFocus = () => setShowCalendar(false);

  const handleKeyDown = e => {
    if (e.which === 8) {
      setValue(prev => prev.slice(0, -1));
    }
  };

  const handleScrollClose = useCallback(() => {
    if (showCalendar && hideOnscroll) {
      setShowCalendar(false);
    }
  }, [showCalendar]);

  useEffect(() => {
    if (showCalendar && hideOnscroll) {
      window.addEventListener("scroll", handleScrollClose, true);
    }
    return () => {
      window.removeEventListener("scroll", handleScrollClose, true);
    };
  }, [showCalendar, handleScrollClose]);

  return (
    <div ref={ref} className="calendar-wrapper">
      <input
        id={id}
        className={cx("form-control", {
          [style["special-disabled"]]: usedForType === "Proposal",
        })}
        type="text"
        placeholder={dateFormat}
        value={value}
        readOnly={usedForType === "Proposal" || isDisabled}
        onChange={e => handleChange(e.target.value)}
        onClick={handleInputClick}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        autoComplete="off"
      />

      <div
        className={cx(
          style["d-flex"],
          style["align-items-center"],
          style["justify-content-center"],
          style["calendar-icon"],
          style["p-2"]
        )}
        onClick={() => setShowCalendar(prev => !prev)}>
        <Icon icon="ui-calendar" />
      </div>

      {showCalendar && (
        <CustomCalendar
          onChange={onChange}
          onClickDay={handleFocus}
          value={calendarValue}
          minDate={usedForType === "Proposal" ? new Date() : minDate}
          maxDate={maxDate}
        />
      )}
    </div>
  );
};

CountdownCalender.propTypes = {
  dateFormat: PropTypes.string.isRequired,
  updateState: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  value: PropTypes.string, // controlled
  defaultValue: PropTypes.string, // fallback
};

export default CountdownCalender;
